import React, { useState, useRef, useEffect } from "react";
import { Download, ChevronDown, FileCode, ImageIcon, FileSpreadsheet } from "lucide-react";

interface ExportMenuProps {
  onExportExcel?: () => void;
  onExportImage?: () => void;
  onExportHTML?: () => void;
}

const ExportMenu: React.FC<ExportMenuProps> = ({ onExportExcel, onExportImage, onExportHTML }) => {
  const [isOpen, setIsOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 bg-white border border-gray-300 hover:bg-gray-50 hover:text-blue-600 text-gray-700 px-3 py-1.5 rounded-lg text-sm font-bold transition-colors shadow-sm ${isOpen ? 'ring-2 ring-blue-100 border-blue-400 text-blue-600' : ''}`}
      >
        <Download size={16} /> 导出
        <ChevronDown size={14} className={`transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl shadow-xl border border-gray-100 overflow-hidden z-[60] animate-in fade-in slide-in-from-top-2 duration-200">
           <div className="p-1 space-y-1">
             {onExportHTML && (
               <button 
                 onClick={() => { onExportHTML(); setIsOpen(false); }}
                 className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-lg text-sm text-slate-700 transition-colors group relative overflow-hidden"
               >
                 <div className="absolute left-0 top-0 bottom-0 w-1 bg-blue-500 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                 <div className="w-8 h-8 rounded-lg bg-orange-100 text-orange-600 flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                    <FileCode size={16} />
                 </div>
                 <div className="flex flex-col">
                   <span className="font-bold flex items-center gap-2">交互式报表 <span className="bg-orange-100 text-orange-700 text-[9px] px-1 rounded border border-orange-200">推荐</span></span>
                   <span className="text-[10px] text-slate-400">含列表与可拖拽甘特图</span>
                 </div>
               </button>
             )}

             {onExportImage && (
              <button 
                onClick={() => { onExportImage(); setIsOpen(false); }}
                className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-lg text-sm text-slate-700 transition-colors group"
              >
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-blue-600 flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <ImageIcon size={16} />
                </div>
                <div className="flex flex-col">
                  <span className="font-bold">高清图片</span>
                  <span className="text-[10px] text-slate-400">当前视图快照 (.png)</span>
                </div>
              </button>
             )}

             {onExportExcel && (
               <>
                 <div className="h-px bg-slate-100 mx-2"></div>
                 <button 
                   onClick={() => { onExportExcel(); setIsOpen(false); }}
                   className="w-full text-left flex items-center gap-3 px-3 py-2.5 hover:bg-slate-50 rounded-lg text-sm text-slate-700 transition-colors group"
                 >
                   <div className="w-8 h-8 rounded-lg bg-green-100 text-green-600 flex items-center justify-center group-hover:bg-green-200 transition-colors">
                      <FileSpreadsheet size={16} />
                   </div>
                   <div className="flex flex-col">
                     <span className="font-bold">Excel 数据源</span>
                     <span className="text-[10px] text-slate-400">原始明细表格</span>
                   </div>
                 </button>
               </>
             )}
           </div>
        </div>
      )}
    </div>
  );
};

export default ExportMenu;
